// Review & post: everything sent for review. Posting is a separate, explicit Admin step (B2).
import { useState, useEffect, useCallback } from "react";
import { Button, Notice, StatusBadge, SkeletonTable } from "./kit/components";
import { T, text } from "./kit/tokens";
import { loadReadyQueue, entriesWithEvidence } from "./api";
import { errText, Chip } from "./shared";
import { whatLabel, npr, DateChip, Amount, flagsMissingEvidence } from "./workflow";

export default function Review({ ctx }) {
    const { data, cal, display, openEntry, version } = ctx;
    const [q, setQ] = useState(null);
    const [err, setErr] = useState("");
    const [withEvidence, setWithEvidence] = useState(new Set());
    const load = useCallback(async () => {
        try {
            setErr(""); const r = await loadReadyQueue(); setQ(r);
            // 2D-1 (D5): staff costs without any evidence file are flagged (posting is still allowed; the reference is required).
            setWithEvidence(await entriesWithEvidence(r.txns.filter(flagsMissingEvidence).map(t => t.journal_entry_id)));
        } catch (e) { setErr(errText(e)); }
    }, []);
    useEffect(() => { load(); }, [load, version]);
    const items = q ? [
        ...q.txns.map(t => ({ key: t.id, entryId: t.journal_entry_id, date: t.txn_date, title: whatLabel(t), sub: [t.party_name, t.description].filter(Boolean).join(" · "), amount: <Amount t={t} />,
            noEvidence: flagsMissingEvidence(t) && !withEvidence.has(t.journal_entry_id) })),
        ...q.manual.map(e => ({ key: e.id, entryId: e.id, date: e.entry_date, title: "Journal entry", sub: e.narration, amount: <span style={text.money}>{npr(e.total_amount || 0)}</span> })),
    ].sort((a, b) => a.date.localeCompare(b.date)) : [];
    return (
        <div>
            <Notice tone="info">Open each item, check the accounting it will create, then post it. Posting gives it a voucher number and makes it permanent; corrections are made by reversing.</Notice>
            {!data.settings.enabled && <div style={{ marginTop: 10 }}><Notice tone="warning">Accounts is off, so nothing can be posted.</Notice></div>}
            <div style={{ height: 14 }} />
            {err && <Notice tone="danger">{err}</Notice>}
            {!q ? <SkeletonTable rows={3} cols={4} /> : items.length === 0 ? (
                <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: T.radius, padding: 28, textAlign: "center" }}>
                    <div style={{ fontWeight: 650, color: T.textStrong }}>Nothing waiting for review</div>
                    <div style={{ ...text.caption, marginTop: 6 }}>Items appear here when someone chooses "Save & send for review".</div>
                </div>
            ) : (
                <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: T.radius, overflow: "hidden" }}>
                    {items.map(it => (
                        <div key={it.key} style={{ display: "flex", gap: 12, alignItems: "center", padding: "12px 16px", borderBottom: `1px solid ${T.borderSoft}`, flexWrap: "wrap" }}>
                            <DateChip iso={it.date} cal={cal} display={display} />
                            <div style={{ flex: "1 1 220px", minWidth: 0 }}>
                                <div style={{ color: T.textStrong, fontWeight: 600 }}>{it.title}</div>
                                <div style={{ ...text.caption, overflowWrap: "anywhere" }}>{it.sub}</div>
                            </div>
                            {it.noEvidence && <Chip tone={T.warning}>No evidence attached</Chip>}
                            {it.amount}
                            <StatusBadge status="ready" />
                            <Button size="sm" onClick={() => openEntry(it.entryId)}>Review</Button>
                        </div>))}
                </div>
            )}
        </div>
    );
}
